import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { multiPolygon, point } from '@turf/helpers';
import type { FeatureCollection, MultiPolygon } from 'geojson';

// In-memory cache for Estonia boundary GeoJSON
let estoniaBoundaryCache: MultiPolygon | null = null;

/**
 * Load Estonia boundary GeoJSON from the public folder
 * Results are cached in memory to avoid repeated fetches
 * Extracts the geometry from the FeatureCollection
 */
async function loadEstoniaBoundary(): Promise<MultiPolygon> {
  if (estoniaBoundaryCache) {
    return estoniaBoundaryCache;
  }

  const response = await fetch('/estonia_geojson.json');
  if (!response.ok) {
    throw new Error('Failed to load Estonia boundary data');
  }

  const geoJson: FeatureCollection = await response.json();

  // Extract the first feature's geometry from the FeatureCollection
  if (!geoJson.features || geoJson.features.length === 0) {
    throw new Error('Estonia boundary data is empty');
  }

  const geometry = geoJson.features[0].geometry as MultiPolygon;
  estoniaBoundaryCache = geometry;
  return geometry;
}

/**
 * Check if given GPS coordinates are within Estonia's boundaries
 *
 * @param latitude - Latitude coordinate
 * @param longitude - Longitude coordinate
 * @returns true if coordinates are within Estonia, false otherwise
 *
 * @example
 * ```typescript
 * const inEstonia = await isWithinEstonia(59.437, 24.745); // Tallinn
 * console.log(inEstonia); // true
 *
 * const notInEstonia = await isWithinEstonia(60.169, 24.938); // Helsinki
 * console.log(notInEstonia); // false
 * ```
 */
export async function isWithinEstonia(latitude: number, longitude: number): Promise<boolean> {
  try {
    const estoniaBoundary = await loadEstoniaBoundary();
    const testPoint = point([longitude, latitude]);

    // Handle both Polygon and MultiPolygon geometries

    const multiPoly = multiPolygon(estoniaBoundary.coordinates);
    return booleanPointInPolygon(testPoint, multiPoly);
  } catch (error) {
    console.error('Error validating GPS location:', error);
    throw new Error('Failed to validate GPS location');
  }
}
